import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle, UserCircle2 } from "lucide-react";
import { useCreateFrontDesk, useCreateStaff } from "./hooks/useStaff";
import {
  CreateFrontDeskCustomRoleUserDto,
  CreateStaffUserDto,
  Role,
} from "@/types/user";
import { useTeams } from "./hooks/useTeams";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  StaffFormData,
  staffSchema,
  getTeamPlaceholder,
} from "./utils/staffSchema";
import {
  FormField,
  SelectField,
} from "../reservations/guests/components/form/FormFields";
import ImageSelector from "../reservations/guests/components/form/ImageSelector";
import MultiSelect from "@/components/common/MultiSelect";
import LanguageSelect from "@/components/common/LanguageSelect";
import { fileService } from "@/services/file-service";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useCustomRoles } from "../users/hooks/useCustomRoles";
import { useTranslation } from "react-i18next";

export const AddStaffPage: React.FC = () => {
  usePageTitle();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hotelId = useCurrentHotelId();
  const createStaff = useCreateStaff();
  const createFrontDesk = useCreateFrontDesk();
  const { data: customRoles = [], isLoading: areCustomRolesLoading } =
    useCustomRoles("tenant");
  const { data: teamsData } = useTeams();
  const { data: teams } = teamsData || {};

  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      photoId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      position: "",
      teams: [] as string[],
      role: "staff",
      customRoleId: "",
      password: "",
      preferredLanguage: "en",
    },
  });

  const selectedTeamIds = watch("teams");
  const selectedImage = watch("photoId");
  const selectedAccountType = watch("role");
  const frontDeskCustomRoles = customRoles.filter(
    (customRole) => customRole.baseRole === Role.FRONT_DESK,
  );
  let frontDeskRolePlaceholder = "Create a Frontdesk role first";
  if (areCustomRolesLoading) {
    frontDeskRolePlaceholder = "Loading roles...";
  } else if (frontDeskCustomRoles.length > 0) {
    frontDeskRolePlaceholder = "Select a Frontdesk role";
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const onSubmit = async (data: StaffFormData) => {
    if (!hotelId) return;
    try {
      let photoId: string | undefined = undefined;
      if (selectedPhotoFile) {
        try {
          photoId =
            await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
        } catch (e) {
          console.error("Failed to upload staff photo:", e);
        }
      }

      const commonUserData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        username: data.email,
        hotelId,
        photoId,
        preferredLanguage: data.preferredLanguage,
      };

      if (data.role === "frontdesk") {
        const userData: CreateFrontDeskCustomRoleUserDto = {
          ...commonUserData,
          customRoleId: data.customRoleId!,
          password: data.password!,
        };
        await createFrontDesk.mutateAsync(userData);
      } else {
        const userData: CreateStaffUserDto = {
          ...commonUserData,
          teams: data.teams || [],
          position: data.position,
        };
        await createStaff.mutateAsync(userData);
      }

      navigate("/staff");
    } catch (err) {
      console.error("Failed to create staff:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => navigate("/staff")}
        >
          <ArrowLeft />
          {t("staff.actions.back", "Back")}
        </Button>

        <div>
          <h1 className="mb-8 text-center text-2xl font-semibold">
            {t("staff.actions.addUser", "Add user")}
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Avatar Upload */}
            <div className="mb-8 flex justify-center">
              <div>
                <ImageSelector
                  defaultIcon={
                    <UserCircle2 className={"size-[72px] stroke-1"} />
                  }
                  overlayContent={
                    <div
                      className={
                        "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white"
                      }
                    >
                      <PlusCircle className={"size-5"} />
                    </div>
                  }
                  onImageSelect={handleImageUpload}
                  selectedImage={selectedImage || undefined}
                  className="mb-4"
                  imageClassName={"size-[72px] rounded-full"}
                />
                {errors.photoId?.message && (
                  <span className="text-center text-red-600">
                    Photo is required
                  </span>
                )}
              </div>
            </div>

            {/* Name and Surname */}
            <div className="grid grid-cols-2 gap-6">
              <FormField
                id="firstName"
                label="NAME"
                required
                register={register}
                error={errors.firstName?.message}
                placeholder="Name"
                className="h-12"
              />
              <FormField
                id="lastName"
                label="SURNAME"
                required
                register={register}
                error={errors.lastName?.message}
                placeholder="Surname"
                className="h-12"
              />
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-2 gap-6">
              <FormField
                id="email"
                label="EMAIL"
                type="email"
                required
                register={register}
                error={errors.email?.message}
                placeholder="@gmail.com"
                className="h-12"
              />
              <FormField
                id="phone"
                label="PHONE"
                type="tel"
                required
                register={register}
                error={errors.phone?.message}
                placeholder="000 000 00 00"
                className="h-12"
              />
            </div>

            {selectedAccountType === "staff" && (
              <FormField
                id="position"
                label="POSITION"
                required
                register={register}
                error={errors.position?.message}
                placeholder="Position"
                className="h-12"
              />
            )}

            {/* Role and Team */}
            <div className="grid grid-cols-2 gap-6">
              <SelectField
                id="role"
                label="ACCOUNT TYPE"
                register={register}
                options={[
                  { value: "staff", label: "Staff (Staff App)" },
                  {
                    value: "frontdesk",
                    label: "Frontdesk (Web access)",
                  },
                ]}
                value={selectedAccountType}
                onValueChange={(value) => {
                  setValue("role", value);
                  if (value === "staff") {
                    setValue("customRoleId", "");
                    setValue("password", "");
                  }
                }}
                className="h-12"
              />

              <LanguageSelect
                label="Language"
                value={watch("preferredLanguage")}
                onChange={(val) => setValue("preferredLanguage", val)}
                className="h-12"
                triggerClassName="h-12"
              />
            </div>

            {selectedAccountType === "frontdesk" && (
              <div className="grid grid-cols-2 gap-6">
                <SelectField
                  id="customRoleId"
                  label="FRONTDESK CUSTOM ROLE"
                  required
                  register={register}
                  options={frontDeskCustomRoles.map((customRole) => ({
                    value: customRole.id,
                    label: customRole.name,
                  }))}
                  value={watch("customRoleId") || ""}
                  onValueChange={(value) => setValue("customRoleId", value)}
                  placeholder={frontDeskRolePlaceholder}
                  error={errors.customRoleId?.message}
                  className="h-12"
                />
                <FormField
                  id="password"
                  label="INITIAL PASSWORD"
                  type="password"
                  required
                  register={register}
                  error={errors.password?.message}
                  placeholder="Minimum 8 characters"
                  className="h-12"
                />
              </div>
            )}

            {selectedAccountType === "staff" && (
              <div className="w-full space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  TEAMS
                </label>
                <MultiSelect
                  options={
                    teams?.map((team) => ({
                      value: team.id,
                      label: team.name,
                    })) || []
                  }
                  value={selectedTeamIds || []}
                  onValueChange={(value) => setValue("teams", value)}
                  placeholder={getTeamPlaceholder(selectedTeamIds)}
                  className="h-12 w-full"
                  containerClassName="!max-w-none"
                />
                {errors.teams?.message && (
                  <p className="text-sm text-red-500">{errors.teams.message}</p>
                )}
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-center pt-4">
              <Button
                type="submit"
                variant="new_default"
                size="xl"
                className="w-full max-w-xs"
                disabled={
                  isSubmitting ||
                  createStaff.isPending ||
                  createFrontDesk.isPending
                }
              >
                {isSubmitting ||
                createStaff.isPending ||
                createFrontDesk.isPending
                  ? t("staff.actions.saving", "Saving...")
                  : t("staff.actions.save", "Save")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
